import { useState } from 'react';
import { ApiError, get, post } from './api';

/**
 * The APEX browser (owner 10/29, admins): every table of a store's APEX files as it really is, page by page, and
 * "Find everywhere" to see where a number or a word appears across a file's tables. Click any value to find it
 * everywhere. Read only; nothing is saved. Used to pick what MyWunderland should show (tell Claude which fields).
 */
interface Col { name: string; kind: string; secret: boolean }
interface Page { table: string; columns: Col[]; rows: (string | number | null)[][]; skip: number; more: boolean }
interface Hit { table: string; columns: string[]; page: Page }
const FILES = [['SALES.GO', 'Sales (receipts, items, payments)'], ['CENTER.GO', 'Center (customers, products, cards, settings)'], ['BOOKINGS.GO', 'Bookings (reservations, events)'],
  ['DAY', 'One day’s file (that day’s heats and races)'], ['CENTERHIST.GO', 'Center history'], ['FILES.GO', 'Files']] as const;

function Grid({ page, mark, onValue }: { page: Page; mark?: string[]; onValue: (v: string) => void }) {
  return (
    <div style={{ overflow: 'auto', maxHeight: 520, border: '1px solid #D9DCD6', borderRadius: 6 }}>
      <table className="apexgrid" style={{ borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
        <thead><tr>{page.columns.map((c) => (
          <th key={c.name} style={{ position: 'sticky', top: 0, background: mark?.includes(c.name) ? '#FFF3C4' : '#F1F3EF', padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid #D9DCD6' }}>{c.name}</th>
        ))}</tr></thead>
        <tbody>{page.rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #EEF0EC' }}>{r.map((v, j) => {
            const c = page.columns[j]!;
            const can = v != null && v !== '' && !c.secret && c.kind !== 'binary';
            return (
              <td key={j} style={{ padding: '3px 8px', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', background: mark?.includes(c.name) ? '#FFF9E0' : undefined, color: v == null ? '#B0B7BE' : undefined }}
                title={can ? `${c.name}: ${v}\nClick to find this value in every table` : String(v ?? '(empty)')}>
                {can ? <button className="linkbtn" style={{ font: 'inherit', color: '#1E5FA8', background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={() => onValue(String(v).replace(/ \(\d{4}\/.*\)$/, ''))}>{String(v)}</button> : String(v ?? '—')}
              </td>
            );
          })}</tr>
        ))}</tbody>
      </table>
      {!page.rows.length && <div style={{ padding: 10, fontSize: 13, color: '#4A5561' }}>No rows.</div>}
    </div>
  );
}

export function ApexBrowser({ stores, showToast }: { stores: { locationId: number; store: string }[]; showToast: (m: string) => void }) {
  const [loc, setLoc] = useState(stores[0]?.locationId ?? 0);
  const [pick, setPick] = useState<string>('BOOKINGS.GO');
  const [dayFile, setDayFile] = useState(() => new Date(Date.now() - 7 * 3600_000).toISOString().slice(0, 10));
  const file = pick === 'DAY' ? `DAY${dayFile.replace(/-/g, '')}.GO` : pick;
  const [tables, setTables] = useState<{ name: string; readable: boolean }[] | null>(null);
  const [canAllow, setCanAllow] = useState(false);
  const [busy, setBusy] = useState('');
  const [page, setPage] = useState<Page | null>(null);
  const [filter, setFilter] = useState<{ col: string; value: string }>({ col: '', value: '' });
  const [find, setFind] = useState('');
  const [found, setFound] = useState<{ value: string; hits: Hit[]; unreadable: string[]; skipped: string[] } | null>(null);
  const q = (extra: Record<string, string | number>) => new URLSearchParams({ location: String(loc), file, ...Object.fromEntries(Object.entries(extra).map(([k, v]) => [k, String(v)])) });
  const err = (e: unknown, m: string) => showToast(e instanceof ApiError ? e.message : m);

  const loadTables = async () => {
    setBusy('tables'); setPage(null); setFound(null);
    try { const r = await get<{ tables: { name: string; readable: boolean }[]; canAllow: boolean }>(`/api/admin/apex/browse/tables?${q({})}`); setTables(r.tables); setCanAllow(r.canAllow); }
    catch (e) { err(e, 'Couldn’t list the tables.'); } finally { setBusy(''); }
  };
  const open = async (table: string, skip = 0, f = filter) => {
    setBusy('table');
    try { setPage((await get<{ page: Page }>(`/api/admin/apex/browse/table?${q({ table, skip, ...(f.col && f.value ? { col: f.col, value: f.value } : {}) })}`)).page); }
    catch (e) { err(e, 'Couldn’t open that table.'); } finally { setBusy(''); }
  };
  const findAll = async (value: string) => {
    setFind(value); setBusy('find');
    try { setFound({ value, ...(await post<{ hits: Hit[]; unreadable: string[]; skipped: string[] }>('/api/admin/apex/browse/find', { location: loc, file, value })) }); }
    catch (e) { err(e, 'The search didn’t finish.'); } finally { setBusy(''); }
  };
  const allow = async (names: string[]) => {
    setBusy('allow');
    try {
      const r = await post<{ granted: string[]; failed: { table: string; why: string }[] }>('/api/admin/apex/browse/allow', { location: loc, file, tables: names });
      showToast(r.granted.length ? `MyWunderland may now read ${r.granted.join(', ')}.` : `Not allowed: ${r.failed.map((f) => `${f.table} (${f.why})`).join(', ')}`);
      await loadTables();
    } catch (e) { err(e, 'The permissions helper didn’t answer.'); } finally { setBusy(''); }
  };
  const locked = (tables ?? []).filter((t) => !t.readable);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #E1E4DE', paddingTop: 12 }}>
      <span style={{ fontSize: 13, color: '#4A5561' }}>
        Every table in a store’s APEX, as APEX keeps it. Pick a file, open a table to read it page by page (newest first), or click any value to find
        where else it appears. Read only: nothing in APEX is changed and nothing is saved here. Passwords and PINs are blanked.
        <b> Find everywhere</b> reads through every table, so use it outside busy hours.
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <select className="sel" value={loc} onChange={(e) => { setLoc(Number(e.target.value)); setTables(null); setPage(null); setFound(null); }} aria-label="Store">
          {stores.map((s) => <option key={s.locationId} value={s.locationId}>{s.store}</option>)}</select>
        <select className="sel" value={pick} onChange={(e) => { setPick(e.target.value); setTables(null); setPage(null); setFound(null); }} aria-label="APEX file">
          {FILES.map(([f, w]) => <option key={f} value={f}>{f === 'DAY' ? 'DAY….GO' : f} · {w}</option>)}</select>
        {pick === 'DAY' && <input className="inp" type="date" value={dayFile} aria-label="Which day" onChange={(e) => { if (e.target.value) { setDayFile(e.target.value); setTables(null); setPage(null); setFound(null); } }} />}
        <button className="btn2" disabled={!!busy} onClick={() => void loadTables()}>{busy === 'tables' ? 'Listing…' : 'Show tables'}</button>
      </div>
      {tables && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {tables.map((t) => (
            <button key={t.name} className={'pill' + (page?.table === t.name ? ' on' : '')} disabled={!!busy || !t.readable} title={t.readable ? 'Open this table' : 'MyWunderland may not read this table yet'}
              onClick={() => { const f = { col: '', value: '' }; setFilter(f); void open(t.name, 0, f); }} style={{ fontSize: 12, opacity: t.readable ? 1 : 0.55 }}>
              {t.readable ? '' : '🔒 '}{t.name}</button>
          ))}
        </div>
      )}
      {locked.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 13, color: '#4A5561' }}>
          <span>🔒 {locked.length} table{locked.length === 1 ? '' : 's'} MyWunderland may not read yet.</span>
          {canAllow ? <button className="btn2" disabled={!!busy} onClick={() => void allow(locked.slice(0, 20).map((t) => t.name))}>{busy === 'allow' ? 'Asking the Beaverton helper…' : `Allow reading ${locked.length > 20 ? 'the next 20' : 'them'}`}</button>
            : <span>The permissions helper on Beaverton isn’t set up, so they can’t be opened from here.</span>}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <input className="inp" value={find} onChange={(e) => setFind(e.target.value)} placeholder="A number or word, e.g. 145687" aria-label="Find everywhere" style={{ width: 240 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && find.trim()) void findAll(find.trim()); }} />
        <button className="btn2" disabled={!!busy || find.trim().length < 2} onClick={() => void findAll(find.trim())}>{busy === 'find' ? 'Searching every table… (up to a minute and a half)' : `Find everywhere in ${file}`}</button>
      </div>
      {found && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <b style={{ fontSize: 14 }}>“{found.value}” found in {found.hits.length} table{found.hits.length === 1 ? '' : 's'} of {file}{found.hits.length ? ' (the matching columns are yellow):' : '.'}</b>
          {found.hits.map((h) => (
            <div key={h.table} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13 }}><b>{h.table}</b> › {h.columns.join(', ')}{h.page.more ? ' (first 5 rows)' : ''} · <button className="linkbtn" style={{ color: '#1E5FA8', background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={() => { const f = { col: h.columns[0]!, value: found.value }; setFilter(f); void open(h.table, 0, f); }}>open all matching rows</button></span>
              <Grid page={h.page} mark={h.columns} onValue={(v) => void findAll(v)} />
            </div>
          ))}
          {found.unreadable.length > 0 && <span style={{ fontSize: 12.5, color: '#4A5561' }}>Not searched (MyWunderland may not read them yet): {found.unreadable.join(', ')}</span>}
          {found.skipped.length > 0 && <span style={{ fontSize: 12.5, color: '#4A5561' }}>Not searched (out of time or an error): {found.skipped.join(', ')}</span>}
        </div>
      )}
      {page && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <b style={{ fontSize: 14 }}>{page.table}</b>
            <span style={{ fontSize: 13, color: '#4A5561' }}>rows {page.skip + 1}–{page.skip + page.rows.length}{filter.col && filter.value ? ` where ${filter.col} = ${filter.value}` : ''}</span>
            <button className="btn2" disabled={!!busy || page.skip === 0} onClick={() => void open(page.table, Math.max(0, page.skip - 50))}>Newer</button>
            <button className="btn2" disabled={!!busy || !page.more} onClick={() => void open(page.table, page.skip + 50)}>Older</button>
            <select className="sel-xs" value={filter.col} onChange={(e) => setFilter({ ...filter, col: e.target.value })} aria-label="Only rows where">
              <option value="">Only rows where…</option>
              {page.columns.filter((c) => !c.secret && c.kind !== 'binary').map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}</select>
            {filter.col && <>
              <input className="inp" value={filter.value} onChange={(e) => setFilter({ ...filter, value: e.target.value })} placeholder="equals…" style={{ width: 160 }}
                onKeyDown={(e) => { if (e.key === 'Enter') void open(page.table, 0); }} />
              <button className="btn2" disabled={!!busy} onClick={() => void open(page.table, 0)}>Show</button>
              <button className="btn2" disabled={!!busy} onClick={() => { const f = { col: '', value: '' }; setFilter(f); void open(page.table, 0, f); }}>Clear</button>
            </>}
          </div>
          <Grid page={page} onValue={(v) => void findAll(v)} />
        </div>
      )}
    </div>
  );
}
